import './bulk-messages-slot-list-item.scss';

import { kebabCase } from 'lodash';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { BulkSlotItemDataFragment } from '../../../../data/types/api/graphql';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { makeKeyDownHandler } from '../../../../data/utils/make-key-down-handler';
import {
  ContextMenuHorizontalPosition,
  ContextMenuVerticalPosition,
  useContextMenu,
} from '../../../../data/utils/use-context-menu';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import {
  ContextMenu,
  ContextMenuItemIcon,
} from '../../../../elements/context-menu/context-menu';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';

type BulkMessagesSlotListItemProps = {
  item: BulkSlotItemDataFragment;
  onClick?: (value: BulkSlotItemDataFragment) => void;
  onDelete?: (value: BulkSlotItemDataFragment) => void;
};

function BulkMessagesSlotListItem({
  item,
  onClick,
  onDelete,
}: BulkMessagesSlotListItemProps) {
  const { getFullName } = useFormatters();
  const intl = useIntl();
  const attributes = useMemo(() => {
    if (onClick) {
      return {
        role: 'button',
        tabIndex: 0,
        onClick: () => onClick(item),
        onKeyDown: makeKeyDownHandler(() => onClick(item)),
      };
    }

    return {};
  }, [item, onClick]);

  const values = {
    ...item,
    day: intl.formatDate(item.appointment.startTime, {
      weekday: 'long',
    }),
    date: intl.formatDate(item.appointment.startTime, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }),
    start: intl.formatDate(item.appointment.startTime, {
      hour: 'numeric',
      minute: 'numeric',
    }),
    end: intl.formatDate(item.appointment.endTime, {
      hour: 'numeric',
      minute: 'numeric',
    }),
    honorific: item.appointment.healthProfessional?.honorific,
    name: getFullName(item.appointment.healthProfessional),
    slots: `${item.nrOfSpotsTaken}/${item.totalNrOfSpots}`,
    canDelete: item.nrOfSpotsTaken > 0,
  };

  const isInPast = useMemo(() => {
    const slotDate = new Date(item.appointment.startTime);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return slotDate < today;
  }, [item.appointment.startTime]);

  const { contextMenuState, onOpenContextMenu } = useContextMenu({
    horizontal: ContextMenuHorizontalPosition.Right,
    vertical: ContextMenuVerticalPosition.Bottom,
  });

  return (
    <div
      {...attributes}
      className={`bulk-messages-slot-list-item${
        isInPast ? ` bulk-messages-slot-list-item--disabled` : ''
      }`}
    >
      <div className="bulk-messages-slot-list-item__content bulk-messages-slot-list-item__content--date">
        <p>{values.day}</p>
        <h3>
          {values.date} {values.start} - {values.end}
        </h3>
      </div>

      <div className="bulk-messages-slot-list-item__content bulk-messages-slot-list-item__content--health-professional">
        <p>
          <CustomFormattedMessage
            id={`common.honorific.${kebabCase(values.honorific)}`}
          />
        </p>
        <h3> {values.name}</h3>
      </div>
      <div className="bulk-messages-slot-list-item__content bulk-messages-slot-list-item__content--slots">
        <p>
          <CustomFormattedMessage id="bulk-messages.slots.title" />
        </p>
        <h3>{values.slots}</h3>
      </div>

      <div className="bulk-messages-slot-list-item__content bulk-messages-slot-list-item__content--action">
        {!isInPast && (
          <Button
            type={ButtonType.TRANSPARENT}
            icon={ButtonIcon.ELLIPSIS_VERTICAL}
            onClick={onOpenContextMenu}
          />
        )}
      </div>

      {contextMenuState && (
        <ContextMenu
          state={contextMenuState}
          items={[
            {
              key: 'delete',
              icon: ContextMenuItemIcon.Delete,
              title: <CustomFormattedMessage id="common.delete" />,
              disabled: values.canDelete,
              onClick: () => {
                onDelete?.(item);
              },
            },
          ]}
        />
      )}
    </div>
  );
}
export { BulkMessagesSlotListItem };
